<template>
  <tr>
    <th>
      SAO-{{ portNumber * 12 + channelNumber + 1 }}
    </th>
    <th v-if="ioMode == 3">
      <template v-if="!hidden && !calibrating">
        <Checkbox binary :disabled="manualPending" :modelValue="channel.manualSelected.value" @click="toggleChannelManual()"></Checkbox>
      </template>
    </th>
    <td style="width: 100%;">
      <div v-if="!hidden" class="align-center">
        <template v-if="view == 'auto'">
          {{ valueString }}
        </template>
        <template v-if="view == 'manual'">
          <InputText
            :class="{ pending: currentVoltage != sliderValue }"
            :modelValue="sliderValue" 
            @click="onTextInputClicked"
            @update:modelValue="onTextInputChanged"
          ></InputText>
          <input type="range" step="0.1" 
            :min="0" 
            :max="10" 
            :value="sliderValue" 
            @input="onSliderInput"
          >
        </template>
        <template v-if="view == 'calibrating'">
          <div style="display: flex; flex-direction: column; padding: 5px; gap: 5px;">
            <div style="display: flex; align-items: center; gap: 5px;">
              Timestamp: 
              <span id="timestamp">
                {{ timestamp || '...' }}
              </span>
              Error factor: 
              <span id="error-factor">
                {{ errorFactorString || '...' }}
              </span>
              Observed voltage: <input type="text" v-model="observedVoltage" style="width: 100px;">
              <button @click="submitCalibration()" :disabled="observedVoltage == null">
                Ok
              </button>
              <button @click="cancelCalibration()">
                Cancel
              </button>
            </div>
          </div>
        </template>
      </div>
    </td>
    <td>
      <button v-if="!hidden && view != 'calibrating'" @click="onClickCalibrate" style="margin: 5px;">
        Calibrate
      </button>
    </td>
  </tr>
</template>

<script>
import { reactive } from "vue";

let shared = reactive({
  selectedComponent: null
});

export default {
  props: ["ioMode", "port", "portNumber", "channel", "channelNumber", "sensorModels"],
  data() {
    return {
      shared,
      calibrating: false,
      manualPending: false,
      timer: null,
      pendingRequest: null,
      sliderValue: getVoltage(this.channel.digitalValue.value),
      sliderValueBeforeCalibration: null,
      closed: false,
      timestamp: null,
      errorFactor: null,
      observedVoltage: null
    }
  },
  unmounted() {
    if (this.calibrating)
      this.cancelCalibration();
  },
  computed: {
    view() {
      if (this.calibrating) return "calibrating";
      else if (this.channel.manualSelected.value) return "manual";
      else return "auto";
    },
    session() {
      return store.sessions[0];
    },
    hidden() {
      return (shared.selectedComponent != null && shared.selectedComponent != this);
    },
    isTimedOut() {
      return (
        this.port.timers.connectionTimer.value == 0 ||
        this.port.channelCounts.value[3] <= this.channelNumber
      );
    },
    currentVoltage() {
      return getVoltage(this.channel.digitalValue.value);
    },
    valueString() {
      if (this.isTimedOut && this.ioMode != 1)
        return "Timed out";
      else
        return this.currentVoltage;
    },
    errorFactorString() {
      return this.errorFactor?.toFixed(3);
    }
  },
  methods: {
    toggleChannelManual() {
      this.manualPending = true;
      store.sessions[0].changeValues([{
        handle: this.channel.manualSelected.handle,
        value: !this.channel.manualSelected.value
      }], () => this.manualPending = false);
    },
    onClickCalibrate() {
      this.calibrating = true;
      this.sliderValueBeforeCalibration = this.sliderValue;
      shared.selectedComponent = this;

      this.pendingRequest?.cancel();
      this.pendingRequest = this.session.queueRequest("/cmd/requestSaoCalibration", {
        portNumber: this.portNumber,
        channelNumber: this.channelNumber,
      }, () => {
        this.waitForCalibration();
      });
    },
    waitForCalibration() {
      this.pendingRequest?.cancel();
      this.pendingRequest = this.session.queueRequest("/cmd/waitForSaoCalibration", { portNumber: this.portNumber }, (response) => {
        if (response) {
          this.timestamp = response.timestamp;
          this.errorFactor = response.errorFactor;
        }
        else {
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.waitForCalibration(), 500);
        }
      });
    },
    submitCalibration() {
      let observedVoltage = this.observedVoltage;
      this.timestamp = null;
      this.errorFactor = null;
      this.observedVoltage = null;

      this.pendingRequest = this.session.queueRequest("/cmd/sendSaoCalibration", { 
        portNumber: this.portNumber, 
        channelNumber: this.channelNumber,
        observedVoltage: parseFloat(observedVoltage)
      }, () => {
        this.waitForCalibration();
      });
    },
    cancelCalibration() {
      clearTimeout(this.timer);
      this.calibrating = false;
      this.pendingRequest?.cancel();
      this.pendingRequest = null;
      this.timestamp = null;
      this.errorFactor = null;
      this.observedVoltage = null;
      shared.selectedComponent = null;
      this.session.queueRequest("/cmd/stopSaoCalibration", {
        portNumber: this.portNumber,
        channelNumber: this.channelNumber
      });

      if (this.channel.manualSelected.value)
        this.setValue(this.sliderValueBeforeCalibration);
    },
    setValue(value) {
      this.sliderValue = value;
      this.pendingRequest?.cancel();
      this.pendingRequest = this.session.queueRequest("/cmd/setAnalogOutput", {
        portNumber: this.portNumber,
        channelNumber: this.channelNumber,
        value: getDigital(this.sliderValue)
      }, (response) => {
        this.pendingRequest = null;
      });
    },
    onTextInputClicked(event) {
      event.target.select();
    },
    onTextInputChanged(value) {
      this.setValue(parseFloat(value) || 0);
    },  
    onSliderInput(event) {
      this.setValue(parseFloat(event.target.value));
    },    
  },
  watch: {
    "ioMode": {
      immediate: true,
      handler() {
        shared.selectedComponent = null;
      }
    },
    "channel.digitalValue.value": {
      handler(value) {
        this.sliderValue = getVoltage(value);
      }
    }
  }
}

function getVoltage(digital) {
  return (digital / 4095 * 10).toFixed(3);
}

function getDigital(voltage) {
  return 4095 / 10 * voltage;
}

</script>

<style scoped>
input.p-inputtext {
  width: 75px;
  text-align: center;
}
input.p-inputtext.pending {
  outline: none;
  border: 2px solid rgb(20, 94, 179);
}
input[type="range"] {
  width: 90%;
  flex-grow: 1;
}
.align-center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding-left: 10px;
  padding-right: 10px;
}
.value-string {
  padding: 0;
  padding-left: 10px;
  padding-right: 10px;
  white-space: nowrap;
}
.value-string.pending {
  font-weight: bold;
  font-style: italic;
}

#timestamp, #error-factor {
  font-weight: bold;
  background: #efefef;
  padding: 5px;
  border-radius: 5px;
}

button {
  border: 1px solid black;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  width: 100px;
  height: 28px;
  cursor: pointer;
}
button.selected {
  color: white;
  background: dodgerblue;
}
</style>